<template>
  <div class="pickup-edit-container">
    <div class="form-title">{{ titleText }}</div>
    
    <!-- 日期選擇欄位 -->
    <div class="form-field">
      <div class="w-full h-[56px] border border-[#AAAAAA] rounded-md">
        <el-date-picker 
          size="large" 
          v-model="selectedDate" 
          type="date" 
          placeholder="選擇日期 *" 
          :disabled-date="disabledDate"
          :editable="false"
          :clearable="false"
          @change="onDateChange">
        </el-date-picker>
      </div>
    </div>

    <!-- 時間選擇欄位 -->
    <div v-if="!isRetailPickUp" class="form-field">
      <div class="w-full bg-white h-[56px] border border-[#AAAAAA] rounded-md relative"
        @click="showTimePicker ? closeTimePicker($event) : displayTimePicker($event)">
        <div class="w-full h-full flex items-center justify-between px-3" v-if="!selectedTime">
          <div class="w-full h-full text-[22px] font-normal flex items-center" style="color: rgba(0, 0, 0, 0.3);">
            選擇時間 *
          </div>
          <text class="button-icon size-7 text-[28px] self-center cursor-pointer"
            style="color: #FBCB02;">expand_more</text>
        </div>
        <div class="w-full h-full flex items-center justify-between text-[22px] font-normal px-3"
          style="color: rgba(0, 0, 0, 0.3);" v-else>
          {{ selectedTime }}
          <text class="button-icon size-7 text-[28px] self-center cursor-pointer"
            style="color: #FBCB02;">expand_more</text>
        </div>
        
        <transition name="time-picker-transition">
          <div class="w-[110%] left-[-5%] absolute top-[64px] bg-white grid grid-cols-4 py-4 px-2 rounded-md time-picker"
            v-if="showTimePicker">
            <div class="flex items-center justify-center mx-1.5 px-4 h-11 border border-[#AAAAAA] rounded-md mb-2"
              v-for="(item, index) in timeOptions" :key="index" @click="pickTime(item, $event)">
              {{ item }}
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import commonUtilities from '@/utils/common.utils';
import { dayjs } from 'element-plus';
import { mapGetters, mapState } from 'vuex'
import {
  ORDER_DAYS_KEY,
  getAvailableDateRange,
  isOutsideAvailableRange
} from '@/utils/orderDate.utils';

export default {
  name: 'PickUpEdit',
  data() {
    return {
      showTimePicker: false,
      selectedDate: '',
      selectedTime: '',
      timeOptions: [],
      // 禁用日期邏輯（復用自 pickUp.vue）
      disabledDate: (d) => {
        const today = dayjs();
        const tomorrow = dayjs().add(1, 'day');
        const selectedDay = dayjs(d);
        const isSameDay = (day1, day2) => dayjs(day1).isSame(day2, 'day');
        // 備貨天數決定最早可取貨日，未來可預約天數自該日起算
        const { firstAvailableDay, lastAvailableDay } = getAvailableDateRange(
          this.preparationDay,
          this.preOrderDays,
          today
        );
        if (isOutsideAvailableRange(selectedDay, { firstAvailableDay, lastAvailableDay })) {
          return true;
        }

        if (this.preOrderDays !== null) {
          if (isSameDay(d, today)) {
            return !this.todayStoreOpen;
          }

          return this.storeCloseByDate(d);
        }

        // 未設定未來可預約天數、但有備貨天數：沿用兩天視窗，改由最早可取貨日起算
        if (this.preparationDay > 0) {
          const secondAvailableDay = firstAvailableDay.add(1, 'day');
          if (!isSameDay(d, firstAvailableDay) && !isSameDay(d, secondAvailableDay)) {
            return true;
          }
          return this.storeCloseByDate(d);
        }

        // 如果今天營業且營業時間未結束
        if (this.todayStoreOpen) {
          // 如果明天也營業
          if (this.tomorrowStoreOpen) {
            return !isSameDay(d, today) && !isSameDay(d, tomorrow);
          } else {
            // 明天不營業
            return !isSameDay(d, today);
          }
        } else {
          // 今天營業時間已結束
          if (this.tomorrowStoreOpen) {
            return !isSameDay(d, tomorrow);
          } else {
            // 今天和明天都不營業
            return true;
          }
        }
      }
    }
  },
  computed: {
    titleText() {
      return this.isRetailPickUp ? '確認取貨日期' : '確認取餐時間'
    },
    isRetailPickUp() {
      return this.$route.params.method === 'retailPickUp'
    },
    ...mapState('cart', {
      payment: 'payment'
    }),
    ...mapGetters('basic', {
      todayBusinessHours: 'todayBusinessHours',
      tomorrowBusinessHours: 'tomorrowBusinessHours',
      todayStoreClose: 'todayStoreClose',
      tomorrowStoreClose: 'tomorrowStoreClose',
      pickUpDelayHour: 'pickUpDelayHour',
      pickUpDelayMinute: 'pickUpDelayMinute',
      retailPickUpDelayHour: 'retailPickUpDelayHour',
      retailPickUpDelayMinute: 'retailPickUpDelayMinute',
      businessHoursByDate: 'businessHoursByDate',
      storeCloseByDate: 'storeCloseByDate',
      preOrderDaysByMethod: 'preOrderDaysByMethod',
      preparationDayByMethod: 'preparationDayByMethod'
    }),
    orderDaysKey() {
      return this.isRetailPickUp
        ? ORDER_DAYS_KEY.RETAIL_PICK_UP
        : ORDER_DAYS_KEY.PICK_UP
    },
    preOrderDays() {
      return this.preOrderDaysByMethod(this.orderDaysKey)
    },
    preparationDay() {
      return this.preparationDayByMethod(this.orderDaysKey)
    },
    currentPickUpDelayHour() {
      return this.isRetailPickUp ? this.retailPickUpDelayHour : this.pickUpDelayHour
    },
    currentPickUpDelayMinute() {
      return this.isRetailPickUp ? this.retailPickUpDelayMinute : this.pickUpDelayMinute
    },
    // 營業時間相關計算（復用自 pickUp.vue）
    todayEndTime() {
      const endTimes = this.todayBusinessHours.map(item => {
        if (item.endTime.split(':')[0] == '00') {
          return `24:${item.endTime.split(':')[1]}`
        }
        return item.endTime
      }).reduce((a, b) => {
        return a > b ? a : b
      });
      return endTimes
    },
    timeOptionStartTomorrow() {
      if (this.todayStoreClose) {
        return true;
      }
      const currentTime = new Date()
      currentTime.setHours(currentTime.getHours() + this.currentPickUpDelayHour, currentTime.getMinutes() + this.currentPickUpDelayMinute)
      const storeEndTime = new Date()
      const [endHour, endMinute] = this.todayEndTime.split(':').map(Number)
      storeEndTime.setHours(endHour, endMinute)
      const lessThanFifteenMinutesOfTodayEndTime = new Date()
      lessThanFifteenMinutesOfTodayEndTime.setHours(endHour, endMinute - 15)
      return currentTime > storeEndTime || currentTime > lessThanFifteenMinutesOfTodayEndTime
    },
    todayStoreOpen() {
      return !this.todayStoreClose && !this.timeOptionStartTomorrow
    },
    tomorrowStoreOpen() {
      return !this.tomorrowStoreClose
    }
  },
  watch: {
    // 監聽 payment 變化，同步更新組件資料
    payment: {
      handler() {
        this.initializeData()
      },
      deep: true
    },
    selectedDate() {
      this.closeTimePicker()
      this.timeOptions = []
      if (this.isRetailPickUp) return
      this.setupTimeOptions()
      if (this.selectedTime && !this.timeOptions.includes(this.selectedTime)) {
        this.selectedTime = ''
        this.updatePaymentTime()
      }
    }
  },
  mounted() {
    // 初始化組件資料
    this.initializeData()
  },
  methods: {
    // 初始化組件資料
    initializeData() {
      // 處理日期：將字串轉換為 Date 物件
      if (this.payment.pickupDate) {
        const dateStr = this.payment.pickupDate
        const dateParts = dateStr.split('/')
        if (dateParts.length === 3) {
          // 創建 Date 物件 (年, 月-1, 日)
          this.selectedDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
        }
      } else {
        this.selectedDate = ''
      }
      
      // 處理時間
      this.selectedTime = this.isRetailPickUp ? '' : (this.payment.pickupTime || '')
      
      // 如果有日期，設定時間選項
      if (this.selectedDate && !this.isRetailPickUp) {
        this.setupTimeOptions()
      }
    },
    
    // 日期變更處理
    onDateChange() {
      this.updatePaymentDate()
      if (this.isRetailPickUp) return
      this.setupTimeOptions()
    },
    
    // 時間選擇處理
    pickTime(time, event) {
      // 阻止事件冒泡，避免觸發父容器的點擊事件
      if (event) {
        event.stopPropagation()
        event.preventDefault()
      }
      this.selectedTime = time
      this.closeTimePicker()
      this.updatePaymentTime()
    },
    
    // 更新付款資訊中的日期
    updatePaymentDate() {
      if (!this.selectedDate) return
      
      const payment = { ...this.payment }
      payment.pickupDate = commonUtilities.convertDate(this.selectedDate)
      if (this.isRetailPickUp) {
        payment.pickupTime = null
      }
      this.$store.commit("cart/setPayment", payment)
    },
    
    // 更新付款資訊中的時間
    updatePaymentTime() {
      const payment = { ...this.payment }
      payment.pickupTime = this.selectedTime
      this.$store.commit("cart/setPayment", payment)
    },
    // 時間選擇相關方法（復用自 pickUp.vue）
    setupTimeOptions() {
      const today = new Date()
      const selectedDate = this.selectedDate || this.payment.pickupDate
      if (!selectedDate) return
      const selectedBusinessHours = this.businessHoursByDate(selectedDate)
      
      // 如果使用者選擇當日日期 
      if (commonUtilities.convertDate(selectedDate) === commonUtilities.convertDate(today)) {
        for (const item of this.todayBusinessHours) {
          this.timeOptionHelper({...item})
        }
      } else {
        for (const item of selectedBusinessHours) {
          this.timeOptionHelper({...item})
        }
      }
      
      // 過濾相同時間
      this.timeOptions = this.timeOptions.filter((item, index, self) => self.indexOf(item) === index)
    },
    timeOptionHelper(item) {
      const today = new Date()
      const selectedDate = this.selectedDate || this.payment.pickupDate
      
      // 如果選擇日期為今天
      if (commonUtilities.convertDate(selectedDate) === commonUtilities.convertDate(today)) {
        const currentHourLargerThanStartHour = Number(item.startTime.split(':')[0]) < today.getHours()
        const currentMinuteLargerThanStartMinute = Number(item.startTime.split(':')[1]) < today.getMinutes()
        const currentHourEqualToStartHour = Number(item.startTime.split(':')[0]) === today.getHours()
        if (currentHourLargerThanStartHour || (currentHourEqualToStartHour && currentMinuteLargerThanStartMinute)) {
          item.startTime = `${today.getHours()}:${today.getMinutes()}`
        }
      }
      
      // 加入店家設定等候時間
      let startHour = parseInt(item.startTime.split(':')[0]) + this.currentPickUpDelayHour;
      let startMinute = parseInt(item.startTime.split(':')[1]) + this.currentPickUpDelayMinute;
      
      if (startMinute >= 60) {
        startHour += 1;
        startMinute -= 60;
      }

      const endHour = parseInt(item.endTime.split(':')[0]);
      const endMinute = parseInt(item.endTime.split(':')[1]);

      for (let hour = startHour; hour <= endHour; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          if (hour === startHour && minute < startMinute) {
            continue;
          }
          if (hour === endHour && minute > endMinute) {
            break;
          }
          let time = `${hour < 10 ? '0' + hour : hour}:${minute < 10 ? '0' + minute : minute}`;
          this.timeOptions.push(time);
        }
      }
    },
    displayTimePicker(event) {
      if (!this.selectedDate && !this.payment.pickupDate) {
        commonUtilities.createToast('info', '請先選擇日期')
        return
      }
      if (event) {
        event.stopPropagation()
        event.preventDefault()
      }
      this.showTimePicker = true
    },
    closeTimePicker(event) {
      if (event) {
        event.stopPropagation()
        event.preventDefault()
      }
      this.showTimePicker = false
    }
  }
}
</script>

<style lang="scss" scoped>
.pickup-edit-container {
  width: 100%;
  padding: 0;
}

.form-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  text-align: center;
}

.form-field {
  margin-bottom: 16px;
}

// 時間選擇器樣式（復用自 pickUp.vue）
.time-picker-transition-enter-active,
.time-picker-transition-leave-active {
  transition: 0.2s ease-in-out;
}

.time-picker-transition-enter-from,
.time-picker-transition-leave-to {
  max-height: 0;
  opacity: 0;
  transform: scaleY(0);
}

.time-picker-transition-enter-to,
.time-picker-transition-leave-from {
  opacity: 1;
  transform: scaleY(1);
}

.time-picker {
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
  -webkit-box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
  -moz-box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
  z-index: 1000;
}

// Element Plus 日期選擇器樣式調整
:deep(.el-date-editor) {
  width: 100%;
  position: relative;
  
  .el-input__prefix {
    position: absolute;
    right: 4px;
    left: auto;
    z-index: 1;
    
    .el-input__prefix-inner {
      .el-icon {
        color: #FBCB02;
        font-size: 28px;
        width: 28px;
        height: 28px;
      }
    }
  }
  
  .el-input__inner {
    padding-left: 2px;
    padding-right: 40px;
  }
}
</style>
